import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { ToastProvider } from './components/ui/Toast';

// Suppress benign ResizeObserver error (common with React Flow and dynamic content)
// This error occurs when resize observations can't complete in one animation frame
// It's harmless and doesn't affect functionality
const isResizeObserverError = (msg: string | Event | undefined): boolean => {
  if (!msg) return false;
  const msgStr = typeof msg === 'string' ? msg : (msg as ErrorEvent).message || '';
  return msgStr.includes('ResizeObserver loop');
};

// Handler for ErrorEvent listener - capture phase to catch it early
const resizeObserverErr = (e: ErrorEvent) => {
  if (isResizeObserverError(e.message)) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
};
window.addEventListener('error', resizeObserverErr, true);

// Also handle via window.onerror for React dev overlay
const originalOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (isResizeObserverError(message)) {
    return true; // Suppress the error
  }
  return originalOnError ? originalOnError(message, source, lineno, colno, error) : false;
};

// Override reportError to suppress ResizeObserver errors from React dev overlay
if (typeof window !== 'undefined' && typeof window.reportError === 'function') {
  const originalReportError = window.reportError.bind(window);
  window.reportError = (error: unknown) => {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('ResizeObserver loop')) {
      return; // Suppress
    }
    originalReportError(error);
  };
}

// Handle unhandled promise rejections that might contain ResizeObserver errors
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && isResizeObserverError(String(e.reason))) {
    e.preventDefault();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const renderWithToast = (children: React.ReactNode) => {
  root.render(
    <React.StrictMode>
      <ToastProvider>{children}</ToastProvider>
    </React.StrictMode>,
  );
};

// Local-dev sandbox routes — bypass auth + the full app shell so the
// editor (or any other isolated component) can be iterated against
// `npm start` without logging in or hitting the backend.
//
// Gated on NODE_ENV !== 'production' so production CRA builds tree-
// shake the dynamic import out of the bundle; the sandbox UI never
// reaches a live env. The dynamic import keeps the dev path async,
// which is fine because /__test/editor is only ever hit from a local
// `npm start` session anyway.
const isEditorSandboxRoute =
  process.env.NODE_ENV !== 'production' &&
  window.location.pathname === '/__test/editor';

const isWelcomeSandboxRoute =
  process.env.NODE_ENV !== 'production' &&
  window.location.pathname === '/__test/welcome';

const isBuildPreviewRoute =
  process.env.NODE_ENV !== 'production' &&
  window.location.pathname === '/__test/build-preview';

const isHomeSandboxRoute =
  process.env.NODE_ENV !== 'production' &&
  window.location.pathname === '/__test/home';

const isToastSandboxRoute =
  process.env.NODE_ENV !== 'production' &&
  window.location.pathname === '/__test/toast';

const isLayeredAnswerSandboxRoute =
  process.env.NODE_ENV !== 'production' &&
  window.location.pathname === '/__test/layered-answer';

const isConnectSandboxRoute = process.env.NODE_ENV !== 'production' && window.location.pathname === '/__test/connect';

if (isConnectSandboxRoute) {
  void import('./components/connect/ConnectAssistantPage').then(({ default: ConnectAssistantPage }) => {
    renderWithToast(<ConnectAssistantPage serverUrl="https://mcp.barktechnologies.tech/mcp" />);
  });
} else if (isEditorSandboxRoute) {
  void import('./__editor_sandbox').then(({ default: EditorSandbox }) => {
    renderWithToast(<EditorSandbox />);
  });
} else if (isWelcomeSandboxRoute) {
  void import('./__welcome_sandbox').then(({ default: WelcomeSandbox }) => {
    renderWithToast(<WelcomeSandbox />);
  });
} else if (isBuildPreviewRoute) {
  void import('./__build_preview').then(({ default: BuildPreviewSandbox }) => {
    renderWithToast(<BuildPreviewSandbox />);
  });
} else if (isHomeSandboxRoute) {
  void import('./__home_sandbox').then(({ default: HomeSandbox }) => {
    renderWithToast(<HomeSandbox />);
  });
} else if (isToastSandboxRoute) {
  void import('./__toast_sandbox').then(({ default: ToastSandbox }) => {
    renderWithToast(<ToastSandbox />);
  });
} else if (isLayeredAnswerSandboxRoute) {
  void import('./__layered_answer_sandbox').then(({ default: LayeredAnswerSandbox }) => {
    renderWithToast(<LayeredAnswerSandbox />);
  });
} else {
  // BAR-40: the real app is wrapped in BrowserRouter so the URL is the
  // source of truth for navigation. The /__test/* sandbox branches above
  // are intentionally left unwrapped — they bypass the app shell.
  renderWithToast(
      <BrowserRouter>
        <App />
      </BrowserRouter>,
  );
}
